import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import './ResponsePanel.css';

/**
 * AI response panel component
 * @param {Object} props - Component props
 * @param {Function} props.onProcessResponse - Process response handler
 * @param {boolean} props.isProcessing - Processing state
 * @param {Object} props.processingResults - Results of last processing
 * @param {string} props.title - Panel title
 */
function ResponsePanel({ onProcessResponse, isProcessing, processingResults, title }) {
  const [response, setResponse] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const textareaRef = useRef(null);
  
  // Reset success state after 3 seconds
  useEffect(() => {
    if (success) {
      const timeout = setTimeout(() => {
        setSuccess(false);
      }, 3000);
      
      return () => clearTimeout(timeout);
    }
  }, [success]);
  
  // Focus textarea when component mounts
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);
  
  // Handle response text change
  const handleResponseChange = (e) => {
    setResponse(e.target.value);
    setError(null);
  };
  
  // Process the AI response
  const handleProcessResponse = async () => {
    if (!response.trim()) {
      setError('Please paste the AI response before processing');
      return;
    }
    
    try {
      // Try to validate JSON format
      try {
        JSON.parse(response);
      } catch (err) {
        setError('Invalid JSON response. Make sure you copied the entire response from the AI.');
        return;
      }
      
      // Process the response
      const result = await onProcessResponse(response);
      
      if (result.success) {
        // Show success message
        setSuccess(true);
        
        // Clear response field on success
        setResponse('');
        setError(null);
      } else if (result.error) {
        setError(result.error);
      }
    } catch (err) {
      console.error('Error processing response:', err);
      setError('An error occurred while processing the response');
    }
  };
  
  // Handle paste event
  const handlePaste = async (e) => {
    // Try to get clipboard data
    try {
      const clipboardData = e.clipboardData || window.clipboardData;
      const text = clipboardData.getData('text');
      
      // Check if it's valid JSON
      try {
        JSON.parse(text);
        // If it's valid JSON, replace the entire content
        e.preventDefault();
        setResponse(text);
        setError(null);
      } catch (err) {
        // Not valid JSON, let default paste behavior happen
      }
    } catch (err) {
      // Clipboard access failed, let default paste behavior happen
      console.warn('Could not access clipboard data:', err);
    }
  };
  
  return (
    <div className="response-panel">
      <div className="response-header">
        <h3>{title || 'AI Response'}</h3>
        <button 
          className="process-btn" 
          onClick={handleProcessResponse}
          disabled={isProcessing || !response.trim()}
        >
          {isProcessing ? 'Processing...' : 'Process Response'}
        </button>
      </div>
      
      <div className="response-content">
        <textarea
          ref={textareaRef}
          value={response}
          onChange={handleResponseChange}
          onPaste={handlePaste}
          placeholder="Paste the AI assistant's response here..."
          disabled={isProcessing}
          className={error ? 'has-error' : ''}
        />
        
        {error && (
          <div className="response-error">
            <p>{error}</p>
          </div>
        )}
        
        {success && (
          <div className="response-success">
            <p>Response processed successfully!</p>
          </div>
        )}
        
        {processingResults && (
          <div className="processing-results">
            <h4>Processing Results</h4>
            <pre>{JSON.stringify(processingResults, null, 2)}</pre>
          </div>
        )}
      </div>
      
      <div className="response-instructions">
        <h4>Instructions:</h4>
        <ol>
          <li>Copy the entire response from your AI assistant</li>
          <li>Paste it in the text area above</li>
          <li>Click "Process Response" to apply the changes to your project</li>
        </ol>
      </div>
    </div>
  );
}

ResponsePanel.propTypes = {
  onProcessResponse: PropTypes.func.isRequired,
  isProcessing: PropTypes.bool,
  processingResults: PropTypes.object,
  title: PropTypes.string
};

ResponsePanel.defaultProps = {
  isProcessing: false
};

export default ResponsePanel;
